import { useState, type FormEvent } from 'react';

export default function AnalyticsLeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    business: '',
    crm: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const response = await fetch('https://readdy.ai/api/form/d6tbtrlm9vk3c28i5g40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formData.name,
          phone: formData.phone,
          business: formData.business,
          crm: formData.crm,
        }).toString(),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', business: '', crm: '' });
        setTimeout(() => setStatus('idle'), 6000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <section id="analytics-lead-form" className="py-10 sm:py-16 lg:py-24 bg-[#F8F9FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-0 rounded-2xl lg:rounded-3xl overflow-hidden border border-[#C5A880]/20">

          {/* Left: Photo + quote */}
          <div className="flex flex-col bg-white">
            {/* Photo — full width, original proportions, no overlay */}
            <div className="w-full">
              <img
                src="https://static.readdy.ai/image/621f76ab0c5d0ae7827482fe9856efef/302a767a30ea50e3e5a17ab93e75e01c.png"
                alt="Ирина Шарова-Ялынпала"
                className="w-full h-auto block"
                style={{ display: 'block' }}
              />
            </div>

            {/* Content below photo */}
            <div className="p-5 sm:p-7 lg:p-10 flex flex-col gap-4 sm:gap-5">
              {/* Person */}
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-11 sm:h-11 bg-[#C5A880] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-user-star-line text-lg sm:text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-[#1A2B4C] font-bold text-sm sm:text-base lg:text-lg font-['Playfair_Display']">Ирина Шарова-Ялынпала</p>
                  <p className="text-gray-500 text-xs sm:text-sm font-['Montserrat'] leading-snug mt-1">
                    Внедряет системы аналитики, которые превращают маркетинг из «творчества» в точную математику прибыли.
                  </p>
                </div>
              </div>

              {/* Quote */}
              <div className="bg-[#F8F9FA] border border-[#C5A880]/20 rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6">
                <i className="ri-double-quotes-l text-2xl sm:text-3xl text-[#C5A880] mb-2 sm:mb-3 block"></i>
                <p className="text-xs sm:text-sm lg:text-base text-gray-700 leading-relaxed font-['Montserrat'] italic">
                  Не позволяйте вашему рекламному бюджету исчезать бесследно. Начните видеть деньги там, где другие видят только графики посещаемости.
                </p>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white p-5 sm:p-7 lg:p-12 border-t border-[#C5A880]/10 lg:border-t-0 lg:border-l lg:border-[#C5A880]/10">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#1A2B4C] mb-2 sm:mb-3 font-['Playfair_Display'] leading-tight">
              Получите план оцифровки бизнеса
            </h2>
            <p className="text-xs sm:text-sm lg:text-base text-gray-600 mb-5 sm:mb-7 font-['Montserrat'] leading-relaxed">
              Оставьте заявку — разберём вашу ситуацию и покажем, как именно ваш бизнес будет выглядеть с системой сквозной аналитики.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form id="analytics-capture-form" className="space-y-3 sm:space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-1.5">
                  Ваше имя <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Иван Иванов"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-[#C5A880] focus:border-transparent transition-all font-['Montserrat']"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-1.5">
                  Телефон <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+7 (999) 123-45-67"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-[#C5A880] focus:border-transparent transition-all font-['Montserrat']"
                />
              </div>

              {/* Business */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-1.5">
                  Тип бизнеса <span className="text-red-500">*</span>
                </label>
                <select
                  name="business"
                  required
                  value={formData.business}
                  onChange={(e) => setFormData({ ...formData, business: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-[#C5A880] focus:border-transparent transition-all font-['Montserrat'] cursor-pointer"
                >
                  <option value="">Выберите тип бизнеса</option>
                  <option value="ecommerce">Интернет-магазин</option>
                  <option value="b2b">B2B / Услуги для бизнеса</option>
                  <option value="b2c">B2C / Услуги для людей</option>
                  <option value="offline">Офлайн-бизнес</option>
                  <option value="other">Другое</option>
                </select>
              </div>

              {/* CRM */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-1.5">
                  Используете ли CRM?
                </label>
                <select
                  name="crm"
                  value={formData.crm}
                  onChange={(e) => setFormData({ ...formData, crm: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl text-sm focus:ring-2 focus:ring-[#C5A880] focus:border-transparent transition-all font-['Montserrat'] cursor-pointer"
                >
                  <option value="">Выберите</option>
                  <option value="bitrix24">Bitrix24</option>
                  <option value="amocrm">amoCRM</option>
                  <option value="other_crm">Другая CRM</option>
                  <option value="no_crm">Не используем CRM</option>
                </select>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-[#C5A880] hover:bg-[#B39770] text-white font-bold py-3.5 sm:py-4 px-6 sm:px-8 rounded-lg sm:rounded-xl transition-all duration-300 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm font-['Montserrat'] mt-1 sm:mt-2"
              >
                {status === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-base sm:text-lg"></i>
                    Отправка...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-arrow-right-line text-base sm:text-lg"></i>
                    Получить план оцифровки бизнеса
                  </span>
                )}
              </button>

              {status === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-3 sm:px-4 py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm flex items-center gap-2 font-['Montserrat']">
                  <i className="ri-checkbox-circle-line text-base sm:text-lg flex-shrink-0"></i>
                  Заявка отправлена! Свяжемся с вами в ближайшее время.
                </div>
              )}
              {status === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 sm:px-4 py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm flex items-center gap-2 font-['Montserrat']">
                  <i className="ri-error-warning-line text-base sm:text-lg flex-shrink-0"></i>
                  Произошла ошибка. Попробуйте ещё раз.
                </div>
              )}

              <p className="text-xs text-gray-400 text-center font-['Montserrat']">
                Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности
              </p>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}
